import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp, Phone, Zap, Target, Calendar, Medal, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_CONFIG = {
  top_closer:       { icon: Trophy,      bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-600',   iconBg: 'bg-amber-500',   label: 'Top Closer'        },
  most_improved:    { icon: TrendingUp,  bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', iconBg: 'bg-emerald-500',  label: 'Most Improved'     },
  call_champion:    { icon: Phone,       bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-600',    iconBg: 'bg-blue-500',     label: 'Call Champion'     },
  streak_master:    { icon: Zap,         bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-600',    iconBg: 'bg-rose-500',     label: 'Streak Master'     },
  consistency_king: { icon: Target,      bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-600',  iconBg: 'bg-violet-500',   label: 'Consistency King'  },
};

// ── Weekly Challenge Card ──────────────────────────────────────────────────────
const WeeklyChallenge = ({ challenge }) => {
  const pct = Math.min(Math.round((challenge.current_avg / (challenge.target || 80)) * 100), 100);
  const met = challenge.current_avg >= (challenge.target || 80);
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-amber-500" />
          <h3 className="text-slate-900 font-semibold text-sm">{challenge.title || 'Weekly Challenge'}</h3>
          {met && <span className="ml-auto text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Achieved!</span>}
        </div>
        <p className="text-slate-400 text-xs mb-4">{challenge.description}</p>
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Team avg this week</span>
            <span className={`font-semibold ${met ? 'text-emerald-600' : 'text-slate-700'}`}>{challenge.current_avg}% <span className="text-slate-400 font-normal">/ {challenge.target}%</span></span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${met ? 'bg-emerald-500' : 'bg-teal-500'}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        {/* Site rankings — Feb 2026 UX ask: WEEKLY excellence keeps
            the exact score alongside the site name so managers can
            benchmark clubs against each other. Monthly view drops the
            score (aspirational not comparative). */}
        {challenge.site_leaderboard?.length > 0 && (
          <div className="space-y-1.5 mt-2" data-testid="weekly-site-leaderboard">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Site rankings</p>
            {challenge.site_leaderboard.map((s, i) => (
              <div key={s.site} className="flex items-center gap-2 text-xs">
                <span className="w-4 text-center text-slate-400">{i + 1}</span>
                <span className="flex-1 text-slate-800 font-semibold truncate">{s.site}</span>
                <span className="text-slate-400">{s.calls}c · {s.reps} rep{s.reps === 1 ? '' : 's'}</span>
                <span className={`font-mono font-semibold ${s.avg_score >= (challenge.target || 80) ? 'text-emerald-600' : 'text-slate-600'}`}>{s.avg_score}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Monthly Challenge Card ─────────────────────────────────────────────────────
const MonthlyChallenge = ({ domainId }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = domainId ? `?domain_id=${domainId}` : '';
    axios.get(`${API}/analytics/monthly-challenge${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
   
  }, [domainId]);

  if (loading) return <Card className="border-slate-200 shadow-sm animate-pulse h-44" />;
  if (!data) return null;

  const pct = Math.min(Math.round((data.current_avg / (data.target || 80)) * 100), 100);
  const met = data.current_avg >= data.target;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="monthly-challenge-card">
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 mb-1">
          <Calendar className="w-4 h-4 text-violet-500" />
          <h3 className="text-slate-900 font-semibold text-sm">{data.challenge_title}</h3>
          <span className="ml-auto text-xs text-slate-400">{data.days_remaining}d left</span>
          {met && <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">On target!</span>}
        </div>
        <p className="text-slate-400 text-xs mb-4">{data.description}</p>
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Month-to-date · {data.total_calls_this_month} calls</span>
            <span className={`font-semibold ${met ? 'text-emerald-600' : 'text-slate-700'}`}>{data.current_avg}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${met ? 'bg-emerald-500' : 'bg-violet-500'}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        {/* Site rankings — Feb 2026 UX ask: this is a MONTHLY EXCELLENCE
            challenge so we show clubs ranked WITHOUT the exact score. */}
        {data.site_leaderboard?.length > 0 && (
          <div className="space-y-1.5 mt-2" data-testid="monthly-site-leaderboard">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Site rankings</p>
            {data.site_leaderboard.map((s, i) => (
              <div key={s.site} className="flex items-center gap-2 text-xs">
                <span className="w-4 text-center text-slate-400">{i + 1}</span>
                <span className="flex-1 text-slate-800 font-semibold truncate">{s.site}</span>
                <span className="text-slate-400">{s.calls}c · {s.reps} rep{s.reps === 1 ? '' : 's'}</span>
                {s.achieved && <Medal className="w-3 h-3 text-amber-400 shrink-0" />}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Badge History ──────────────────────────────────────────────────────────────
const BadgeHistorySection = ({ domainId }) => {
  const { token } = useAuth();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const params = domainId ? `?domain_id=${domainId}` : '';
    axios.get(`${API}/analytics/badge-history${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setHistory(r.data.history || []))
      .catch(() => {})
      .finally(() => setLoading(false));
   
  }, [domainId]);

  if (loading) return <Card className="border-slate-200 shadow-sm animate-pulse h-40" />;
  if (!history?.length) return null;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="badge-history">
      <button
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors rounded-xl"
        onClick={() => setExpanded(e => !e)}
      >
        <Star className="w-4 h-4 text-amber-500" />
        <h3 className="text-slate-900 font-semibold text-sm flex-1 text-left">Badge History</h3>
        <span className="text-slate-400 text-xs">{history.length} month{history.length !== 1 ? 's' : ''}</span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <CardContent className="pt-0 pb-5">
          <div className="space-y-5">
            {history.map(month => (
              <div key={month.month}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-slate-500 text-xs font-semibold">{month.month}</span>
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-slate-400 text-xs">{month.total_calls} calls · {month.team_avg} avg</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {month.badges.map((b, i) => {
                    const cfg = BADGE_CONFIG[b.badge] || BADGE_CONFIG.call_champion;
                    const Icon = cfg.icon;
                    return (
                      <div key={`${month.month}-${b.badge}-${i}`}
                        className={`${cfg.bg} border ${cfg.border} rounded-xl p-3 flex flex-col items-center gap-1.5 text-center`}
                        data-testid={`badge-history-${month.month}-${b.badge}`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${cfg.iconBg} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <p className={`text-xs font-semibold ${cfg.text}`}>{b.label}</p>
                        <p className="text-slate-600 text-xs leading-tight">{b.agent_name}</p>
                        <p className="text-slate-400 text-[11px]">{b.value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// ── Main Tab ───────────────────────────────────────────────────────────────────
const GamificationTab = ({ domainId = null }) => {
  const { token, userIndustry } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = domainId ? `?domain_id=${domainId}` : '';
    axios.get(`${API}/analytics/gamification${params}`, { headers: { Authorization: `Bearer ${token}` }, timeout: 25000 })
      .then(r => setData(r.data))
      .catch(() => setData({ achievements: [], streaks: [], weekly_challenge: {} }))
      .finally(() => setLoading(false));
   
  }, [domainId, userIndustry]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
    </div>
  );

  const { achievements = [], streaks = [], weekly_challenge = {} } = data || {};

  return (
    <div className="space-y-6" data-testid="gamification-tab">

      {/* Active Challenges */}
      <div>
        <h2 className="text-slate-700 text-sm font-semibold uppercase tracking-wide mb-3">Active Challenges</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {weekly_challenge?.target && <WeeklyChallenge challenge={weekly_challenge} />}
          <MonthlyChallenge domainId={domainId} />
        </div>
      </div>

      {/* Current Badges */}
      {achievements.length > 0 && (
        <div>
          <h2 className="text-slate-700 text-sm font-semibold uppercase tracking-wide mb-3">Current Badges</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {achievements.map(a => {
              const cfg = BADGE_CONFIG[a.badge] || BADGE_CONFIG.call_champion;
              const Icon = cfg.icon;
              return (
                <Card key={a.badge} className={`${cfg.bg} border ${cfg.border} shadow-sm`} data-testid={`badge-${a.badge}`}>
                  <CardContent className="pt-4 pb-3 flex flex-col items-center gap-2 text-center">
                    <div className={`w-10 h-10 rounded-xl ${cfg.iconBg} flex items-center justify-center shadow`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <p className={`text-xs font-semibold ${cfg.text}`}>{a.label}</p>
                    <p className="text-slate-600 text-xs">{a.agent_name}</p>
                    <p className="text-slate-400 text-[11px]">{a.value}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Streak Leaderboard */}
      {streaks.length > 0 && (
        <div>
          <h2 className="text-slate-700 text-sm font-semibold uppercase tracking-wide mb-3">Streak Leaderboard</h2>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="pt-4 pb-2">
              <div className="divide-y divide-slate-100">
                {streaks.slice(0, 8).map((s, i) => (
                  <div key={s.agent_id} className="flex items-center gap-3 py-2.5" data-testid={`streak-${s.agent_id}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border shrink-0
                      ${i === 0 ? 'bg-amber-100 text-amber-700 border-amber-200'
                      : i === 1 ? 'bg-slate-100 text-slate-600 border-slate-200'
                      : i === 2 ? 'bg-orange-100 text-orange-600 border-orange-200'
                      : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                      {i + 1}
                    </span>
                    <span className="flex-1 text-slate-700 text-sm truncate">{s.agent_name}</span>
                    <span className="text-slate-400 text-xs">{s.avg_score} avg</span>
                    <div className="flex items-center gap-1">
                      <Zap className="w-3 h-3 text-amber-500" />
                      <span className="text-slate-700 text-sm font-semibold">{s.max_streak}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Badge History */}
      <BadgeHistorySection domainId={domainId} />

      {!achievements.length && !streaks.length && (
        <Card className="border-dashed border-slate-200">
          <CardContent className="py-12 flex flex-col items-center gap-2 text-center">
            <Trophy className="w-10 h-10 text-slate-300" />
            <p className="text-slate-500 text-sm">No gamification data yet</p>
            <p className="text-slate-400 text-xs">Grade more calls to start earning badges</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GamificationTab;
